import React from "react";
import { graphql, useFragment } from "react-relay";

import { faEllipsisV } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faCheck } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faChevronRight } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { identity } from "lodash";

import { Button } from "~/ui";
import { IconButton } from "~/ui/legacyFellowIndex";
import Menu from "~/ui/menu";

import { ActionTextContext } from "./modal";
import { SmartStarButton } from "./StarButton";
import { SmartTemplateAuthor } from "./TemplateAuthor";

import type { TemplateCardFullsizeNoteTemplate$key } from "~/__generated__/TemplateCardFullsizeNoteTemplate.graphql";
import type { TemplateCardSmallNoteTemplate$key } from "~/__generated__/TemplateCardSmallNoteTemplate.graphql";

import styles from "./css/TemplateCard.module.css";

export type CardActionProps = (
	| { withActions?: false }
	| { withActions: true; apply: () => void; preview: () => void }
) & {
	onClick?: () => void;
	cardDropdownMenuActions?: { cb: () => void; text: string; icon: IconDefinition; destructive?: boolean }[];
};
export type TemplateCardProps = CardActionProps &
	(
		| { small?: false; template: TemplateCardFullsizeNoteTemplate$key; selected?: boolean }
		| { small: true; template: TemplateCardSmallNoteTemplate$key }
	);

/**
 * Render a template card.
 *
 * This component will probably also need to be passed props for whether it
 * should have actions, and the functions for preview and apply.
 */
export function TemplateCard(props: TemplateCardProps) {
	const { small: _small, template: _template, ...actionProps } = props;

	if (props.small) return <SmallCard template={props.template} {...actionProps} />;
	else return <FullSizeCard template={props.template} selected={props.selected} {...actionProps} />;
}

const formatter = Intl.NumberFormat("en", { notation: "compact", maximumSignificantDigits: 1 });

/**
 * Renders a full-size card.
 */
function FullSizeCard({
	template: templateKey,
	cardDropdownMenuActions,
	selected = false,
	...actionProps
}: { template: TemplateCardFullsizeNoteTemplate$key; selected?: boolean } & CardActionProps) {
	const template = useFragment(
		graphql`
			fragment TemplateCardFullsizeNoteTemplate on NoteTemplateNode {
				title
				starred
				summary
				appliedCount
				author {
					...TemplateAuthor_Author
				}
				...StarButtonNoteTemplate
			}
		`,
		templateKey,
	);
	const { title, summary } = template;

	const [isMenuOpen, setIsMenuOpen] = React.useState(false);
	const stopPropagation = React.useCallback<React.MouseEventHandler<HTMLButtonElement | HTMLDivElement>>(
		e => e.stopPropagation(),
		[],
	);
	const header = React.useMemo(
		() =>
			!!cardDropdownMenuActions && (
				<Menu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
					<Menu.Trigger asChild>
						<IconButton
							icon={faEllipsisV}
							className={cx(styles.dropdownActions, { [styles.stayOpen]: isMenuOpen })}
							onClick={stopPropagation}
						/>
					</Menu.Trigger>
					<Menu.Content>
						{cardDropdownMenuActions.map(({ icon, cb, text, destructive }) => (
							<Menu.Item key={text} destructive={destructive} onSelect={cb} icon={icon} onClick={stopPropagation}>
								<span>{text}</span>
							</Menu.Item>
						))}
					</Menu.Content>
				</Menu>
			),
		[cardDropdownMenuActions, isMenuOpen, stopPropagation],
	);

	const appliedCount = React.useMemo(() => {
		if (template.appliedCount < 10) return null;
		return formatter.format(template.appliedCount);
	}, [template]);

	const footer = React.useMemo(
		() => (
			<CardFooter>
				<div>
					<SmartTemplateAuthor author={template.author} />
				</div>
				{selected ? (
					<FontAwesomeIcon icon={faCheck} fixedWidth className={styles.selectedCheck} />
				) : (
					<>
						<SmartStarButton template={template} className={styles.star} />
						{appliedCount && <div className={styles.applied}>Used {appliedCount}+</div>}
					</>
				)}
			</CardFooter>
		),
		[template, selected, appliedCount],
	);

	return (
		<Card
			title={title}
			description={summary}
			selected={selected}
			header={header}
			footer={footer}
			starred={template.starred}
			{...actionProps}
		/>
	);
}

/**
 * Renders a smaller card. Small cards can't have headers.
 *
 * There could still be actions though.
 */
function SmallCard({ template, ...actionProps }: { template: TemplateCardSmallNoteTemplate$key } & CardActionProps) {
	const { title, summary } = useFragment(
		graphql`
			fragment TemplateCardSmallNoteTemplate on NoteTemplateNode {
				title
				summary
			}
		`,
		template,
	);

	return <Card small title={title} description={summary} {...actionProps} />;
}

interface BaseCardProps {
	title: string;
	description: string;
	selected?: boolean;
	starred?: boolean;
}

type CardSizeProps = { small?: false; header?: React.ReactNode; footer?: React.ReactNode } | { small: true };
type CardProps = BaseCardProps & CardActionProps & CardSizeProps;

/**
 * A component providing card styling and structure. It can optionally have a
 * header, footer, and/or actions.
 */
function Card({ title, description, selected, onClick, starred, ...rest }: CardProps) {
	const primaryText = React.useContext(ActionTextContext);
	const descNode = <p className={styles.cardDescription}>{description}</p>;
	return (
		<article
			className={cx(styles.card, {
				[styles.selected]: selected,
				[styles.small]: rest.small,
				[styles.clickable]: !!onClick,
				[styles.starred]: starred,
				[styles.hasActions]: rest.withActions,
			})}
			onClick={onClick}
			data-test-id="note-template-card"
		>
			<WithActionsHoverable>
				{rest.small || rest.header}
				<h1 className={styles.cardTitle} title={title}>
					{title}
				</h1>
				{!rest.withActions ? (
					descNode
				) : (
					<WithActions
						primaryText={primaryText}
						primaryAction={rest.apply}
						secondaryText="Preview"
						secondaryAction={rest.preview}
						small={rest.small}
					>
						{descNode}
					</WithActions>
				)}
				{rest.small || rest.footer}
			</WithActionsHoverable>
		</article>
	);
}

/**
 * Structure for the footer of a card. It does the styling to put one element
 * on the left and one on the right.
 *
 * Ignores any children after the first 2.
 */
function CardFooter({ children }: React.PropsWithChildren<{}>) {
	if (!children) return null;
	const [left, right] = React.Children.map(children, identity) ?? [null, null];
	return (
		<footer className={styles.cardFooter}>
			<div className={styles.left}>{left}</div>
			<div className={styles.right}>{right}</div>
		</footer>
	);
}

function WithActionsHoverable({ children }: React.PropsWithChildren<{}>) {
	return <div className={styles.withActions}>{children}</div>;
}

/**
 * This component should be a descendant of WithActionsHoverable.
 *
 * Wraps its children with a div that, when WithActionsHoverable is hovered,
 * covers them with two action buttons. The left button is primary and the
 * right secondary.
 */
function WithActions({
	children,
	primaryText,
	primaryAction,
	secondaryText,
	secondaryAction,
	small,
}: React.PropsWithChildren<{
	primaryText: React.ReactNode;
	primaryAction: () => void;
	secondaryText: React.ReactNode;
	secondaryAction: () => void;
	small?: boolean;
}>) {
	return (
		<div className={cx(styles.withActionsCovered, { [styles.small]: small })}>
			{children}
			<div className={cx(styles.showOnHover, styles.actions)}>
				<div className={styles.space}>&nbsp;</div>
				<div className={styles.actionButtons}>
					<Button
						size="wide"
						variant="primary"
						// eslint-disable-next-line react/jsx-no-bind
						onClick={e => (e.stopPropagation(), primaryAction())}
						className={cx(styles.actionButton, styles.primaryActionButton)}
						data-test-id="note-template-card-apply"
					>
						{primaryText}
					</Button>
					<Button
						size="wide"
						variant="secondary"
						// eslint-disable-next-line react/jsx-no-bind
						onClick={e => (e.stopPropagation(), secondaryAction())}
						className={styles.actionButton}
						data-test-id="note-template-card-preview"
					>
						{secondaryText}
					</Button>
				</div>
			</div>
		</div>
	);
}

export function CreateCard({ onClick }: { onClick?: () => void }) {
	return (
		<Button size="wide" className={styles.createCard} onClick={onClick} data-test-id="template-card-create-button">
			<p className={styles.createCardText}>Didn&rsquo;t find what you were looking for?</p>
			<p className={styles.createCardCTA}>
				<span>Create a template</span>
				<FontAwesomeIcon icon={faChevronRight} />
			</p>
		</Button>
	);
}
